/**
 * Frontend error reporter — POSTs uncaught JS errors and unhandled promise
 * rejections to /api/v1/activity-logs/errors, where they're sanitized and
 * stored in db.error_logs.
 *
 * Wired up once at app boot via setupErrorReporter().
 *
 * Privacy:
 *  - We always strip query strings from window.location.pathname before
 *    sending the route.
 *  - The backend re-sanitizes the stack to redact secrets.
 *  - Reporting is best-effort — if the user is unauthenticated or the
 *    network fails, we drop silently rather than create a feedback loop.
 */
import api from '../api/client';

let installed = false;
let recentMessages = new Set();
const DEDUPE_WINDOW_MS = 5000;

// Known-benign browser noise that is NOT an app bug and must not pollute the
// Errors log. These are transient, auto-retried conditions emitted by the
// browser itself (PWA background update-check blips, ResizeObserver settle
// loops). Suppressing them keeps the Errors tab actionable.
const IGNORED_PATTERNS = [
  /Failed to (update|register|unregister) a ServiceWorker/i,
  /An unknown error occurred when fetching the script/i,
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i,
];

function isIgnoredNoise(message) {
  const text = String(message || '');
  return IGNORED_PATTERNS.some((re) => re.test(text));
}

function shouldReport(message) {
  if (!message) return false;
  // Avoid feedback loops if reporter itself errors
  if (typeof message === 'string' && message.includes('/activity-logs/errors')) return false;
  // Drop known-benign, non-actionable browser noise
  if (isIgnoredNoise(message)) return false;
  // Soft dedupe — don't spam server with repeats of the same message
  const key = String(message).slice(0, 200);
  if (recentMessages.has(key)) return false;
  recentMessages.add(key);
  setTimeout(() => recentMessages.delete(key), DEDUPE_WINDOW_MS);
  return true;
}

async function send({ message, stack, severity = 'error' }) {
  if (!shouldReport(message)) return;
  try {
    const route = (window.location?.pathname || '/') + (window.location?.hash || '');
    await api.post('/activity-logs/errors', {
      message: String(message).slice(0, 2000),
      stack_trace: stack ? String(stack).slice(0, 8000) : null,
      route,
      severity,
      user_agent: navigator?.userAgent?.slice(0, 400),
    });
  } catch (err) {
    // Reporter must NEVER throw — that would create an infinite error loop
    // (window.onerror → report fails → window.onerror …). Swallow silently
    // in production; emit a console hint in dev so the swallow is debuggable.
    if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && console.debug) {
      console.debug('errorReporter swallow:', err?.message || err);
    }
  }
}

export function setupErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    send({
      message: event.message || 'Unknown error',
      stack: event.error?.stack,
      severity: 'error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason?.message || String(reason || 'Unhandled promise rejection');
    send({
      message,
      stack: reason?.stack,
      severity: 'error',
    });
  });
}
